import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts';
import { ChildProfile } from '../../models/childProfile';
import {
  AttendanceDataService,
  AttendanceRecord,
} from '../../services/attendanceDataService';
import { ScheduleDataService } from '../../services/scheduleDataService';
import { ScheduleSession } from '../schedule/SessionDetailPanel';
import { occursOnDate } from '../schedule/scheduleOccurrences';
import { toDateString, getMonday } from '../../services/dateUtils';
import './charts.scss';

// ── Helpers ────────────────────────────────────────────────────────────────

const ON_TIME_GRACE_MINUTES = 5;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Mirror Dashboard.tsx — stored stably here so this page works standalone.
function timeToMins(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function addDays(d: Date, n: number): Date {
  const c = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  c.setDate(c.getDate() + n);
  return c;
}

type Timing = 'on-time' | 'early' | 'late';
function classifyTiming(scheduledMins: number, actualMins: number, grace: number): Timing {
  const delta = actualMins - scheduledMins;
  if (Math.abs(delta) <= grace) return 'on-time';
  return delta < 0 ? 'early' : 'late';
}

// No-show comments are stored as "Reason — free text" by AttendanceConfirmModal.
// Bucket the reason prefix into a short label for the pie chart.
function bucketNoShowReason(comment: string | null): string {
  if (!comment) return 'Unspecified';
  const head = comment.split('—')[0].trim().toLowerCase();
  if (!head) return 'Unspecified';
  if (head.includes('ill') || head.includes('sick')) return 'Illness';
  if (head.includes('holiday') || head.includes('vac')) return 'Holiday';
  if (head.includes('family')) return 'Family';
  if (head.includes('appoint')) return 'Appointment';
  if (head.includes('transport')) return 'Transport';
  if (head.includes('unauth')) return 'Unauthorised';
  return 'Other';
}

function shortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00');
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

// ── Palette ────────────────────────────────────────────────────────────────

const COLOURS = {
  attended: '#26923f',
  noShow:   '#b8302f',
  unlogged: '#aaaaaa',
  early:    '#e8a92e',
  onTime:   '#7cb342',
  late:     '#ea7c30',
  scheduled: '#9ec5a6',
  bar:      '#26923f',
  axis:     '#7e8a78',
};

const REASON_PALETTE = [
  '#b8302f', '#e8a92e', '#7cb342', '#3f7fa3',
  '#a06bce', '#ea7c30', '#7e8a78',
];

// ── Range options ──────────────────────────────────────────────────────────

interface RangeOption { key: string; label: string; weeks: number; }
const RANGE_OPTIONS: RangeOption[] = [
  { key: '4',  label: '4 wks',  weeks: 4  },
  { key: '8',  label: '8 wks',  weeks: 8  },
  { key: '13', label: '13 wks', weeks: 13 },
  { key: '26', label: '26 wks', weeks: 26 },
];

// ── Component ──────────────────────────────────────────────────────────────

interface Props {
  childProfiles: ChildProfile[];
  childIdFilter: string | null;
}

const AttendanceCharts: React.FC<Props> = ({ childProfiles, childIdFilter }) => {
  const [range, setRange] = useState<RangeOption>(RANGE_OPTIONS[1]);
  const [sessions, setSessions] = useState<ScheduleSession[]>([]);
  const [cancelledByLocalId, setCancelledByLocalId] = useState<Map<string, Set<string>>>(new Map());
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Whitelist-aware set of child IDs the current user is allowed to see.
  // Home.fetchChildProfiles has already filtered `childProfiles` to the
  // user's whitelist (when one applies), so deriving from it gives us the
  // pre-filter we want for *all* chart data. Admins / unrestricted users
  // get the full org list, so this is a no-op for them.
  const allowedChildIds = useMemo(
    () => new Set(childProfiles.map(c => c.id)),
    [childProfiles],
  );

  const childId = childIdFilter ? Number(childIdFilter) : null;
  const selectedChild = useMemo(
    () => (childId == null ? null : childProfiles.find(c => c.id === childId) ?? null),
    [childId, childProfiles],
  );

  // Date window: end on yesterday (today is partial), start `weeks * 7` days back.
  const { fromDate, toDate, monthsLabel } = useMemo(() => {
    const today = new Date();
    const monday = getMonday(today);
    const end = addDays(monday, 6); // Sunday of current week
    const start = addDays(monday, -((range.weeks - 1) * 7));
    return {
      fromDate: toDateString(start),
      toDate: toDateString(end),
      monthsLabel: `${shortDate(toDateString(start))} – ${shortDate(toDateString(end))}`,
    };
  }, [range]);

  // Hydrate schedule items + exceptions once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [items, exceptions] = await Promise.all([
          ScheduleDataService.listScheduleItems(),
          ScheduleDataService.listScheduleExceptions(),
        ]);
        if (cancelled) return;

        const localIdByServer = new Map<number, string>();
        const localSessions: ScheduleSession[] = items.map((it, i) => {
          const localId = `chart-session-${i + 1}`;
          localIdByServer.set(it.serverId, localId);
          return {
            id: localId, lane: 0,
            dayOfWeek: it.dayOfWeek,
            startTime: it.startTime, endTime: it.endTime,
            childId: it.childId,
            teacherIds: [...it.teacherIds],
            locationIds: [...it.locationIds],
            frequency: it.frequency,
            startDate: it.startDate, endDate: it.endDate,
            serverId: it.serverId,
          };
        });

        const cancelMap = new Map<string, Set<string>>();
        for (const ex of exceptions) {
          if (ex.status !== 'cancelled') continue;
          const localId = localIdByServer.get(ex.scheduleItemServerId);
          if (!localId) continue;
          const set = cancelMap.get(localId) ?? new Set<string>();
          set.add(ex.date);
          cancelMap.set(localId, set);
        }

        setSessions(localSessions);
        setCancelledByLocalId(cancelMap);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to hydrate schedules for charts', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-fetch attendance when range or child changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    AttendanceDataService.listAttendance(fromDate, toDate, childId != null ? { childId } : undefined)
      .then(rows => {
        if (cancelled) return;
        setAttendance(rows);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch attendance for charts', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [fromDate, toDate, childId]);

  // ── Project scheduled occurrences across the date window ─────────────────

  type Occurrence = {
    date: string;          // YYYY-MM-DD
    weekStart: string;     // YYYY-MM-DD (Mon)
    childId: number;
    serverId: number;
    startMins: number;
    endMins: number;
    durationMins: number;
    dayIdx: number;        // 0=Mon..6=Sun
    attendance: AttendanceRecord | null;
  };

  const occurrences: Occurrence[] = useMemo(() => {
    if (sessions.length === 0) return [];
    const out: Occurrence[] = [];
    const startD = new Date(fromDate + 'T00:00');
    const endD = new Date(toDate + 'T00:00');
    const filterChildId = childId;
    for (let d = new Date(startD); d.getTime() <= endD.getTime(); d.setDate(d.getDate() + 1)) {
      const dateStr = toDateString(d);
      const monday = toDateString(getMonday(d));
      const dayIdxRaw = d.getDay();
      const dayIdx = dayIdxRaw === 0 ? 6 : dayIdxRaw - 1; // Mon=0, Sun=6
      for (const s of sessions) {
        if (s.serverId === undefined || s.childId == null) continue;
        if (!allowedChildIds.has(s.childId)) continue;
        if (filterChildId != null && s.childId !== filterChildId) continue;
        if (!occursOnDate(s, new Date(dateStr + 'T00:00'))) continue;
        if (cancelledByLocalId.get(s.id)?.has(dateStr)) continue;
        const startMins = timeToMins(s.startTime);
        const endMins = timeToMins(s.endTime);
        const att = attendance.find(
          a =>
            a.scheduleItemServerId === s.serverId &&
            a.childId === s.childId &&
            a.attendanceDate === dateStr,
        ) ?? null;
        out.push({
          date: dateStr,
          weekStart: monday,
          childId: s.childId,
          serverId: s.serverId,
          startMins,
          endMins,
          durationMins: Math.max(0, endMins - startMins),
          dayIdx,
          attendance: att,
        });
      }
    }
    return out;
  }, [sessions, attendance, cancelledByLocalId, fromDate, toDate, childId, allowedChildIds]);

  // ── Chart data derivations ───────────────────────────────────────────────

  // 1. Weekly attendance rate (line) + scheduled count
  const weeklyTrend = useMemo(() => {
    const byWeek = new Map<string, { scheduled: number; attended: number; noShow: number }>();
    for (const o of occurrences) {
      const slot = byWeek.get(o.weekStart) ?? { scheduled: 0, attended: 0, noShow: 0 };
      slot.scheduled += 1;
      if (o.attendance?.actualArrival) slot.attended += 1;
      else if (o.attendance) slot.noShow += 1;
      byWeek.set(o.weekStart, slot);
    }
    return Array.from(byWeek.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([weekStart, v]) => ({
        week: shortDate(weekStart),
        attendanceRate: v.scheduled === 0 ? 0 : Math.round((v.attended / v.scheduled) * 100),
        scheduled: v.scheduled,
        attended: v.attended,
        noShow: v.noShow,
      }));
  }, [occurrences]);

  // 2. Outcome donut
  const outcomeBreakdown = useMemo(() => {
    let attended = 0, noShow = 0, unlogged = 0;
    const today = toDateString(new Date());
    for (const o of occurrences) {
      if (o.attendance?.actualArrival) attended += 1;
      else if (o.attendance) noShow += 1;
      else if (o.date < today) unlogged += 1;
    }
    return [
      { name: 'Attended', value: attended, fill: COLOURS.attended },
      { name: 'No-show', value: noShow, fill: COLOURS.noShow },
      { name: 'Not logged', value: unlogged, fill: COLOURS.unlogged },
    ].filter(d => d.value > 0);
  }, [occurrences]);

  // 3. Punctuality stacked bar (arrivals + departures)
  const punctuality = useMemo(() => {
    let arrEarly = 0, arrOnTime = 0, arrLate = 0;
    let depEarly = 0, depOnTime = 0, depLate = 0;
    for (const o of occurrences) {
      const att = o.attendance;
      if (att?.actualArrival) {
        const t = classifyTiming(o.startMins, timeToMins(att.actualArrival), ON_TIME_GRACE_MINUTES);
        if (t === 'early') arrEarly += 1;
        else if (t === 'late') arrLate += 1;
        else arrOnTime += 1;
      }
      if (att?.actualDeparture) {
        const t = classifyTiming(o.endMins, timeToMins(att.actualDeparture), ON_TIME_GRACE_MINUTES);
        if (t === 'early') depEarly += 1;
        else if (t === 'late') depLate += 1;
        else depOnTime += 1;
      }
    }
    return [
      { name: 'Arrival',   Early: arrEarly, 'On time': arrOnTime, Late: arrLate },
      { name: 'Departure', Early: depEarly, 'On time': depOnTime, Late: depLate },
    ];
  }, [occurrences]);

  // 4. No-show reasons pie
  const noShowReasons = useMemo(() => {
    const counts = new Map<string, number>();
    for (const o of occurrences) {
      if (!o.attendance) continue;
      if (o.attendance.actualArrival) continue; // attended
      const bucket = bucketNoShowReason(o.attendance.arrivalComment);
      counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], i) => ({ name, value, fill: REASON_PALETTE[i % REASON_PALETTE.length] }));
  }, [occurrences]);

  // 5. By day of week
  const byDayOfWeek = useMemo(() => {
    const buckets = WEEKDAYS.map(d => ({ day: d, scheduled: 0, attended: 0, rate: 0 }));
    for (const o of occurrences) {
      buckets[o.dayIdx].scheduled += 1;
      if (o.attendance?.actualArrival) buckets[o.dayIdx].attended += 1;
    }
    for (const b of buckets) {
      b.rate = b.scheduled === 0 ? 0 : Math.round((b.attended / b.scheduled) * 100);
    }
    return buckets;
  }, [occurrences]);

  // 6. Hours per week (scheduled vs actual attended)
  const hoursPerWeek = useMemo(() => {
    const byWeek = new Map<string, { scheduled: number; actual: number }>();
    for (const o of occurrences) {
      const slot = byWeek.get(o.weekStart) ?? { scheduled: 0, actual: 0 };
      slot.scheduled += o.durationMins;
      const att = o.attendance;
      if (att?.actualArrival && att?.actualDeparture) {
        slot.actual += Math.max(0, timeToMins(att.actualDeparture) - timeToMins(att.actualArrival));
      } else if (att?.actualArrival) {
        // Arrived but no sign-out: count up to scheduled end as a conservative proxy.
        slot.actual += Math.max(0, o.endMins - timeToMins(att.actualArrival));
      }
      byWeek.set(o.weekStart, slot);
    }
    return Array.from(byWeek.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([weekStart, v]) => ({
        week: shortDate(weekStart),
        Scheduled: +(v.scheduled / 60).toFixed(1),
        Attended: +(v.actual / 60).toFixed(1),
      }));
  }, [occurrences]);

  // ── Top-level summary numbers ────────────────────────────────────────────

  const summary = useMemo(() => {
    const total = occurrences.length;
    const attended = occurrences.filter(o => o.attendance?.actualArrival).length;
    const noShow = occurrences.filter(o => o.attendance && !o.attendance.actualArrival).length;
    const rate = total === 0 ? 0 : Math.round((attended / total) * 100);
    return { total, attended, noShow, rate };
  }, [occurrences]);

  // ── Render ───────────────────────────────────────────────────────────────

  const titleScope = selectedChild
    ? `${selectedChild.first_name} ${selectedChild.last_name}`
    : 'All children';

  return (
    <div className="atc-root">
      <div className="atc-header">
        <div>
          <h2 className="atc-title">Attendance Stats</h2>
          <div className="atc-subtitle">{titleScope} · {monthsLabel}</div>
        </div>
        <div className="atc-spacer" />
        <div className="btn-group btn-group-sm" role="group" aria-label="Date range">
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.key}
              type="button"
              className={`btn ${range.key === opt.key ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setRange(opt)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="atc-summary">
        <div className="atc-stat">
          <div className="atc-stat__value">{summary.rate}%</div>
          <div className="atc-stat__label">Attendance rate</div>
        </div>
        <div className="atc-stat">
          <div className="atc-stat__value">{summary.attended}</div>
          <div className="atc-stat__label">Sessions attended</div>
        </div>
        <div className="atc-stat">
          <div className="atc-stat__value">{summary.noShow}</div>
          <div className="atc-stat__label">No-shows</div>
        </div>
        <div className="atc-stat">
          <div className="atc-stat__value">{summary.total}</div>
          <div className="atc-stat__label">Sessions scheduled</div>
        </div>
      </div>

      {loading && occurrences.length === 0 ? (
        <div className="atc-empty">Loading attendance data…</div>
      ) : occurrences.length === 0 ? (
        <div className="atc-empty">
          No scheduled sessions in this range{selectedChild ? ` for ${selectedChild.first_name}` : ''}.
        </div>
      ) : (
        <div className="atc-grid">
          <ChartCard title="Weekly attendance rate" subtitle="% of scheduled sessions actually attended">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={weeklyTrend} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e6e0d2" />
                <XAxis dataKey="week" stroke={COLOURS.axis} fontSize={12} />
                <YAxis stroke={COLOURS.axis} fontSize={12} domain={[0, 100]} unit="%" />
                <Tooltip formatter={((v: any, name: any) =>
                  name === 'attendanceRate' ? [`${v}%`, 'Attendance'] : [v, name]) as any} />
                <Line
                  type="monotone"
                  dataKey="attendanceRate"
                  stroke={COLOURS.attended}
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: COLOURS.attended }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Outcomes" subtitle="Attended vs no-show vs unlogged sessions">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Tooltip />
                <Legend verticalAlign="bottom" height={28} />
                <Pie
                  data={outcomeBreakdown}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                  label={({ percent }) => `${Math.round((percent ?? 0) * 100)}%`}
                  labelLine={false}
                >
                  {outcomeBreakdown.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Punctuality" subtitle={`±${ON_TIME_GRACE_MINUTES} min grace from scheduled time`}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={punctuality} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e6e0d2" />
                <XAxis dataKey="name" stroke={COLOURS.axis} fontSize={12} />
                <YAxis stroke={COLOURS.axis} fontSize={12} allowDecimals={false} />
                <Tooltip />
                <Legend verticalAlign="bottom" height={28} />
                <Bar dataKey="Early"   stackId="a" fill={COLOURS.early} />
                <Bar dataKey="On time" stackId="a" fill={COLOURS.onTime} />
                <Bar dataKey="Late"    stackId="a" fill={COLOURS.late} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="No-show reasons"
            subtitle={noShowReasons.length === 0 ? 'No no-shows in this period' : 'Distribution of recorded reasons'}
          >
            {noShowReasons.length === 0 ? (
              <div className="atc-card__empty">Nothing to show — no no-shows logged.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={28} />
                  <Pie
                    data={noShowReasons}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={90}
                    label={({ name, percent }) => `${name} ${Math.round((percent ?? 0) * 100)}%`}
                    labelLine={false}
                  >
                    {noShowReasons.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title="Attendance by day of week" subtitle="% attended per weekday">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={byDayOfWeek} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e6e0d2" />
                <XAxis dataKey="day" stroke={COLOURS.axis} fontSize={12} />
                <YAxis stroke={COLOURS.axis} fontSize={12} domain={[0, 100]} unit="%" />
                <Tooltip
                  formatter={((v: any, name: any, p: any) =>
                    name === 'rate'
                      ? [`${v}% (${p?.payload?.attended ?? 0}/${p?.payload?.scheduled ?? 0})`, 'Attendance']
                      : [v, name]) as any}
                />
                <Bar dataKey="rate" fill={COLOURS.bar} radius={[6, 6, 0, 0]}>
                  <LabelList dataKey="rate" position="top" formatter={(v) => `${v}%`} fontSize={11} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Hours per week" subtitle="Scheduled vs actual attended hours">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={hoursPerWeek} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e6e0d2" />
                <XAxis dataKey="week" stroke={COLOURS.axis} fontSize={12} />
                <YAxis stroke={COLOURS.axis} fontSize={12} unit="h" />
                <Tooltip />
                <Legend verticalAlign="bottom" height={28} />
                <Bar dataKey="Scheduled" fill={COLOURS.scheduled} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Attended"  fill={COLOURS.attended}  radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}
    </div>
  );
};

interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

const ChartCard: React.FC<ChartCardProps> = ({ title, subtitle, children }) => (
  <div className="atc-card">
    <div className="atc-card__head">
      <h3 className="atc-card__title">{title}</h3>
      {subtitle && <div className="atc-card__sub">{subtitle}</div>}
    </div>
    <div className="atc-card__body">{children}</div>
  </div>
);

export default AttendanceCharts;
